import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// Static annotation definitions extracted outside the component to avoid
// re-creating this object on every render.
const STATIC_ANNOTATIONS = {
  unAnnNatVariants: { text: 'Variant position', color: 'rgb(178, 24, 43)' },
  unMotives: { text: 'Motive', color: '#e63946' },
  pfamDomainPosHmm: { text: 'PFAM-HMM Pos', color: '#aa1c7d' },
  unLigandsBindingSite: { text: 'Binding site', color: '#00b159' },
  unAnnPropActiveSite: { text: 'Active site', color: '#f37735' },
  unGlycosylations: [
    { text: 'N-linked glycosylation', color: '#56ab2f' },
    { text: 'O-linked glycosylation', color: '#9d4edd' },
    { text: 'C-linked glycosylation', color: '#d90368' },
    { text: 'S-linked glycosylation', color: '#00b4d8' },
  ],
  unLipidations: [
    { text: 'S-palmitoyl cysteine', color: '#007f5f' },
    { text: 'N-myristoyl glycine', color: '#2a9d8f' },
    { text: 'S-farnesyl cysteine', color: '#e9c46a' },
    { text: 'S-geranylgeranyl cysteine', color: '#f4a261' },
    { text: 'GPI-anchor', color: '#264653' },
    { text: 'Cholesterol ester', color: '#e76f51' },
    { text: 'Lipoylation', color: '#8ab17d' },
    { text: 'S-stearoyl cysteine', color: '#457b9d' },
    { text: 'O-palmitoleyl serine', color: '#1d3557' },
    { text: 'O-octanoyl serine', color: '#6a4c93' },
  ],
  unModifiedRes: { text: 'Modified Residue', color: '#ffc425' },
  unDisulfideBongBridges: { text: 'Disulfide bond bridge', color: '#8338ec' },
  unCrossLinks: { text: 'CrossLink', color: '#00aedb' },
  inBindingRegion: { text: 'Binding Region', color: '#ff006e' },
  afResiduePredictedInteraction: { text: 'AF-Predicted residue', color: '#008000' },
  None: { text: '', color: '#F9FAFD' },
  unSecondaryStructure: [
    { text: 'Alpha helix', color: '#838ffc' },
    { text: 'Beta strand', color: '#da54a3' },
    { text: 'Turn', color: '#fca311' },
  ],
  unMoleculeProcessing: [
    { text: 'Mature protein', color: '#838ffc' },
    { text: 'Signal peptide', color: '#da54a3' },
    { text: 'Transit peptide', color: '#820e44' },
    { text: 'Propeptide', color: '#c746d4' },
    { text: 'Initiator methionine', color: '#20b2aa' },
    { text: 'Active peptide', color: '#f3722c' },
  ],
  unLocation: [
    { text: 'Topological domain', color: 'rgb(12, 116, 137)' },
    { text: 'Transmembrane region', color: '#da54a3' },
    { text: 'Intramembrane region', color: '#820e44' },
  ],
  afPredictedSecondaryStructure: [
    { text: 'Alpha helix', color: '#FF0080' },
    { text: '3_10 helix', color: '#FF0080' },
    { text: 'Pi helix', color: '#FF0080' },
    { text: 'Polyproline helix', color: '#FF0080' },
    { text: 'Beta strand', color: '#FFC800' },
    { text: 'Turn', color: '#00B266' },
    { text: 'Bend', color: '#66D8C9' },
    { text: 'Coil', color: '#adb5bd' },
    { text: 'Disordered', color: '#e63946' },
  ],
  phosPtms: [
    { text: 'Phosphorylation', color: '#4393C3' },
    { text: 'Acetylation', color: '#5aae61' },
    { text: 'Ubiquitination', color: '#bf812d' },
    { text: 'SUMOylation', color: '#d6604d' },
    { text: 'Methylation', color: '#c51b7d' },
    { text: 'O-GalNAc glycosylation', color: '#e08214' },
    { text: 'O-GlcNAc glycosylation', color: '#fdb863' },
    { text: 'Other', color: '#666666' },
    { text: 'Multiple PTMs', color: '#66D8C9' },
  ],
  pLDDT: [
    { text: 'V. High', color: '#0053d6' },
    { text: 'High', color: '#65cbf3' },
    { text: 'Low ', color: '#ffdb13' },
    { text: 'V. Low', color: '#ff7d45' },
  ],
  amMean: [
    { text: 'Likely Pathogenic', color: '#db0909' },
    { text: 'Ambiguous', color: '#f1c40f' },
    { text: 'Likely Benign ', color: '#0984e3' },
  ],
  AlphaFill: [
    { text: 'V. High', color: '#0053d6' },
    { text: 'High', color: '#65cbf3' },
    { text: 'Low', color: '#ffdb13' },
    { text: 'V. Low', color: '#ff7d45' },
  ],
  hydrophobicity: [
    { text: 'Hydrophilic', color: '#a50026' },
    { text: 'Hydrophobic', color: '#006837' },
  ],
  surface: [
    { text: 'Buried', color: '#3361E1' },
    { text: 'Exposed', color: '#BF2222' },
  ],
};

// Annotation keys whose items are built dynamically from props.
const DYNAMIC_ANNOTATIONS = new Set(['unDomains', 'interPro']);

/**
 * De-duplicates an array of legend items using `text` as the key.
 * Preserves the first occurrence's order.
 */
const dedupeByText = (list) =>
  Array.from(new Map(list.map((item) => [item.text, item])).values());

/**
 * Builds the list of legend items to render for the given annotation.
 */
const buildItems = (annotation, colors, domainInterPro) => {
  if (annotation === 'unDomains') {
    const list = Object.entries(colors || {})
      .filter(([text]) => text !== '-')
      .map(([text, color]) => ({
        text: text.replace(/\s+/g, ' '),
        color: `#${color}`,
      }));
    return dedupeByText(list);
  }

  if (annotation === 'interPro') {
    const list = (domainInterPro || [])
      .filter(({ domainName }) => domainName !== '-')
      .map(({ domainName, color }) => ({
        text: domainName.replace(/\s+/g, ' '),
        color: `#${color}`,
      }));
    return dedupeByText(list);
  }

  const entry = STATIC_ANNOTATIONS[annotation];
  if (!entry) return [];
  return Array.isArray(entry) ? entry : [entry];
};

const FunctionalAnnotationStructureLegend = ({
  annotation,
  colors = {},
  domainInterPro = [],
}) => {
  // Recompute only when the relevant inputs change. For static annotations,
  // `colors` and `domainInterPro` are ignored by buildItems.
  const items = useMemo(
    () => buildItems(annotation, colors, domainInterPro),
    [annotation, colors, domainInterPro],
  );

  const textSizeClass = annotation === 'interPro' ? 'fs--3' : 'fs--2';

  return (
    <div className="d-flex flex-column">
      {items.map((item) => (
        <div
          key={`${item.text}-${item.color}`}
          className="d-flex align-items-center mb-2"
        >
          <div
            className="burble"
            style={{ background: item.color, marginRight: '2px' }}
          />
          <p className={`mb-0 text-start ${textSizeClass}`}>{item.text}</p>
        </div>
      ))}
    </div>
  );
};

FunctionalAnnotationStructureLegend.propTypes = {
  annotation: PropTypes.string.isRequired,
  colors: PropTypes.object,
  domainInterPro: PropTypes.arrayOf(
    PropTypes.shape({
      domainName: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
};

export default React.memo(FunctionalAnnotationStructureLegend);

// Exported for testing / reuse
export { STATIC_ANNOTATIONS, DYNAMIC_ANNOTATIONS, buildItems };
